/**
 * ContractorsPage - Standalone page for the Selected Contractors Database
 * 
 * This is a top-level route (/contractors) that is NOT project-specific.
 * The contractors database is a company-wide resource, not tied to any project.
 * 
 * Users can access this page:
 * - From the main dashboard/home sidebar
 * - From within a project (via navigation link in project sidebar)
 */

import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SelectedContractors from './SelectedContractors';
import ContractorSelectionCriteriaModal from './ContractorSelectionCriteriaModal';
import Toast from '../Layout/Toast';
import { useToast } from '../../hooks/useToast';

const ContractorsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast, showToast, hideToast } = useToast();
  
  // Modal state
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  
  // Check if user came from a project (for back navigation)
  const fromProject = location.state?.fromProject;
  const fromProjectId = location.state?.projectId;
  
  // Handlers
  const handleOpenCriteriaModal = useCallback(() => {
    setShowCriteriaModal(true);
  }, []);
  
  const handleCloseCriteriaModal = useCallback(() => {
    setShowCriteriaModal(false);
  }, []);
  
  const handleSaveSuccess = useCallback((message) => {
    showToast(message || 'Saved successfully', 'success');
  }, [showToast]);
  
  const handleSaveError = useCallback((message) => {
    showToast(message || 'Save failed', 'error');
  }, [showToast]);
  
  const handleBackToProject = useCallback(() => {
    if (fromProjectId) {
      navigate(`/project/${fromProjectId}`);
    } else {
      navigate(-1);
    }
  }, [navigate, fromProjectId]);
  
  return (
    <div style={{ 
      minHeight: '100%',
      backgroundColor: '#f5f7fa'
    }}>
      {/* Back to Project button (only shown when navigated from project) */}
      {fromProject && (
        <div style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e9ecef',
          padding: '0.75rem 1.5rem'
        }}>
          <button
            onClick={handleBackToProject}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← Back to Project
          </button>
        </div>
      )}
      
      {/* Main Content */}
      <div style={{ padding: '0' }}>
        <SelectedContractors 
          onOpenCriteriaModal={handleOpenCriteriaModal}
        />
      </div>
      
      {/* Contractor Selection Criteria Modal */}
      {showCriteriaModal && (
        <ContractorSelectionCriteriaModal
          isOpen={true}
          onClose={handleCloseCriteriaModal}
          onSaveSuccess={handleSaveSuccess}
          onSaveError={handleSaveError}
        />
      )}
      
      {/* Toast Notification */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
        duration={5000}
      />
    </div>
  );
};

export default ContractorsPage;
